import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DayPicker } from "react-day-picker";
import "react-day-picker/style.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  Layers3,
  Users,
} from "lucide-react";
import {
  fetchUserActivitySummary,
  fetchUserLoginHistory,
  fetchUsers,
} from "../apis/users";
import { displayToast } from "../utils/displayToast";
import "../styles/components/Dashboard.css";

const SESSION_COLORS = {
  active: "#10b981",
  logged_out: "#60a5fa",
  expired: "#f43f5e",
  forced_logout: "#8b5cf6",
  idle: "#f59e0b",
};

const ACTIVITY_RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "last24hours", label: "Last 24 Hours" },
  { value: "last7days", label: "Last 7 Days" },
  { value: "custom", label: "Custom Date" },
];

const CALENDAR_MONTHS = Array.from({ length: 12 }, (_, month) =>
  new Date(2000, month).toLocaleDateString([], { month: "long" }),
);

const getSummaryValue = (summary, keys) => {
  for (const key of keys) {
    if (summary?.[key] !== undefined && summary?.[key] !== null) {
      return Number(summary[key]) || 0;
    }
  }
  return 0;
};

const formatHour = (date) =>
  date.toLocaleTimeString([], { hour: "numeric" }).replace(" ", "");

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseLocalDate = (value, endOfDay = false) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
};

const buildLoginActivity = (events, range, customStart, customEnd) => {
  const now = new Date();
  let start;
  let end = now;
  let bucketCount;
  let bucketMilliseconds;

  if (range === "today") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    bucketCount = now.getHours() + 1;
    bucketMilliseconds = 60 * 60 * 1000;
  } else if (range === "last7days") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
    bucketCount = 7;
    bucketMilliseconds = 24 * 60 * 60 * 1000;
  } else if (range === "custom") {
    start = parseLocalDate(customStart);
    const requestedEnd = parseLocalDate(customEnd, true);
    end = requestedEnd < now ? requestedEnd : now;
    const isSingleDay = customStart === customEnd;
    bucketMilliseconds = isSingleDay ? 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    bucketCount = isSingleDay
      ? end.getHours() + 1
      : Math.floor(
          (new Date(end.getFullYear(), end.getMonth(), end.getDate()) - start) /
            bucketMilliseconds,
        ) + 1;
  } else {
    const currentHour = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      now.getHours(),
    );
    start = new Date(currentHour.getTime() - 23 * 60 * 60 * 1000);
    bucketCount = 24;
    bucketMilliseconds = 60 * 60 * 1000;
  }

  const buckets = Array.from({ length: bucketCount }, (_, index) => {
    const bucketDate = new Date(start.getTime() + index * bucketMilliseconds);
    return {
      timestamp: bucketDate.getTime(),
      label:
        range === "today" ||
        (range === "custom" && bucketMilliseconds === 60 * 60 * 1000)
          ? formatHour(bucketDate)
          : range === "last7days" || range === "custom"
            ? bucketDate.toLocaleDateString([], {
                day: "numeric",
                month: "short",
              })
            : `${bucketDate.toLocaleDateString([], { day: "numeric", month: "short" })} ${formatHour(bucketDate)}`,
      count: 0,
    };
  });

  events.forEach((event) => {
    const eventTime = new Date(event.event_time).getTime();
    if (
      Number.isNaN(eventTime) ||
      eventTime < start.getTime() ||
      eventTime > end.getTime()
    ) {
      return;
    }
    const bucketIndex = Math.floor(
      (eventTime - start.getTime()) / bucketMilliseconds,
    );
    if (buckets[bucketIndex]) buckets[bucketIndex].count += 1;
  });

  return buckets;
};

const normalizeSessionCounts = (summary) => {
  const source =
    summary.sessions ||
    summary.session_status_counts ||
    summary.session_status_summary ||
    summary.session_status ||
    summary.session_breakdown ||
    summary.session_summary ||
    summary.session_counts ||
    summary.sessions_by_status ||
    summary.sessionStatusCounts;

  if (Array.isArray(source)) {
    return source.map((item) => ({
      key: String(item.status || item.name || item.type || "unknown")
        .toLowerCase()
        .replace(/[\s-]+/g, "_"),
      name: String(item.status || item.name || item.type || "Unknown")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()),
      value: Number(item.count ?? item.value ?? item.total ?? 0),
    }));
  }

  if (source && typeof source === "object") {
    return Object.entries(source).map(([key, value]) => ({
      key: key.toLowerCase().replace(/[\s-]+/g, "_"),
      name: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()),
      value: Number(value) || 0,
    }));
  }

  const flatCounts = {
    active: summary.active_session_count ?? summary.active_sessions_count,
    logged_out: summary.logged_out_sessions ?? summary.logged_out,
    expired: summary.expired_sessions ?? summary.expired,
    forced_logout: summary.forced_logout_sessions ?? summary.forced_logout,
    idle: summary.idle_sessions ?? summary.idle_session_count,
  };
  return Object.entries(flatCounts)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => ({
      key,
      name: key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase()),
      value: Number(value) || 0,
    }));
};

const fetchLoginEvents = async () => {
  const usersResponse = await fetchUsers("?page=1&count=100");
  const users = usersResponse?.items || [];
  const historyResponses = await Promise.all(
    users.map((user) => fetchUserLoginHistory(user.id, "?page=1&count=100")),
  );
  return historyResponses
    .flatMap((response) => response?.items || [])
    .filter(
      (event) =>
        String(event.event_type).toUpperCase() === "LOGIN_SUCCESS" &&
        event.event_time,
    );
};

const Dashboard = ({ currentUser }) => {
  const navigate = useNavigate();
  const calendarControlRef = useRef(null);
  const today = new Date();
  const [summary, setSummary] = useState({});
  const [loginEvents, setLoginEvents] = useState([]);
  const [activityRange, setActivityRange] = useState("today");
  const [customStartDate, setCustomStartDate] = useState(
    formatDateInput(today),
  );
  const [customEndDate, setCustomEndDate] = useState(formatDateInput(today));
  const [isRangeMenuOpen, setIsRangeMenuOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(today);
  const [isMonthMenuOpen, setIsMonthMenuOpen] = useState(false);
  const [isYearMenuOpen, setIsYearMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isCalendarOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (
        calendarControlRef.current &&
        !calendarControlRef.current.contains(event.target)
      ) {
        setIsCalendarOpen(false);
        setIsMonthMenuOpen(false);
        setIsYearMenuOpen(false);
        setPendingStartDate(null);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () =>
      document.removeEventListener("pointerdown", handleOutsideClick);
  }, [isCalendarOpen]);

  useEffect(() => {
    let isMounted = true;

    const loadDashboard = async () => {
      try {
        setIsLoading(true);
        const [summaryResponse, loginEventResponse] = await Promise.all([
          fetchUserActivitySummary(),
          fetchLoginEvents(),
        ]);
        if (!isMounted) return;

        if (summaryResponse?.description && !summaryResponse?.data) {
          displayToast(summaryResponse.description, "error");
        } else {
          setSummary(
            summaryResponse?.data ||
              summaryResponse?.summary ||
              summaryResponse ||
              {},
          );
        }
        setLoginEvents(loginEventResponse);
      } catch (error) {
        displayToast("Failed to load dashboard activity", "error");
        console.error(error);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadDashboard();
    return () => {
      isMounted = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cards = [
    {
      label: "Total Users",
      value: getSummaryValue(summary, ["total_users", "totalUsers"]),
      icon: Users,
      tone: "blue",
    },
    {
      label: "Online Users",
      value: getSummaryValue(summary, ["online_users", "onlineUsers"]),
      icon: CircleDot,
      tone: "green",
      note: "Currently active",
    },
    {
      label: "Idle Users",
      value: getSummaryValue(summary, ["idle_users", "idleUsers"]),
      icon: Clock3,
      tone: "amber",
      note: "Logged in but inactive",
    },
    {
      label: "Active Sessions",
      value: getSummaryValue(summary, [
        "active_sessions",
        "activeSessions",
        "active_session_count",
      ]),
      icon: Layers3,
      tone: "violet",
      note: "Across all users",
    },
  ];
  const sessionData = normalizeSessionCounts(summary);
  const idleUsers = getSummaryValue(summary, ["idle_users", "idleUsers"]);
  const idleIndex = sessionData.findIndex((item) => item.key === "idle");
  if (idleIndex >= 0) {
    sessionData[idleIndex] = {
      ...sessionData[idleIndex],
      value: idleUsers,
    };
  } else {
    sessionData.push({ key: "idle", name: "Idle", value: idleUsers });
  }
  const totalSessions = sessionData.reduce(
    (total, item) => total + item.value,
    0,
  );
  const loginActivity = buildLoginActivity(
    loginEvents,
    activityRange,
    customStartDate,
    customEndDate,
  );
  const activityTitle =
    activityRange === "today"
      ? "Today's Login Activity"
      : activityRange === "last7days"
        ? "Last 7 Days Login Activity"
        : activityRange === "custom"
          ? "Custom Date Login Activity"
          : "Last 24 Hours Login Activity";

  const customDateRange = pendingStartDate
    ? { from: pendingStartDate, to: undefined }
    : {
        from: parseLocalDate(customStartDate),
        to: parseLocalDate(customEndDate),
      };

  const handleCustomDayClick = (day) => {
    if (!pendingStartDate) {
      setPendingStartDate(day);
      return;
    }

    setCustomStartDate(formatDateInput(pendingStartDate));
    setCustomEndDate(formatDateInput(day));
    setPendingStartDate(null);
    setIsCalendarOpen(false);
  };

  const selectActivityRange = (value) => {
    setActivityRange(value);
    setIsRangeMenuOpen(false);
    setPendingStartDate(null);
    setIsCalendarOpen(value === "custom");
    if (value === "custom") {
      setCalendarMonth(parseLocalDate(customStartDate));
    }
  };

  const calendarYears = Array.from(
    { length: today.getFullYear() - 1999 },
    (_, index) => today.getFullYear() - index,
  );

  const changeCalendarMonth = (offset) => {
    const nextMonth = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth() + offset,
    );
    if (nextMonth >= new Date(2000, 0) && nextMonth <= today) {
      setCalendarMonth(nextMonth);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <main className="dashboard-container activity-dashboard">
        <header className="activity-dashboard-header">
          <div>
            <h1>Dashboard</h1>
            <p>Overview of your organisation</p>
          </div>
        </header>

        <section className="activity-welcome-strip">
          <div>
            <strong>Welcome back, {currentUser || "Admin"}!</strong>
            <span>Here is your organisation activity overview for today.</span>
          </div>
          <span className="activity-system-status">
            <i /> System Online
          </span>
        </section>

        <section className="activity-summary-grid" aria-busy={isLoading}>
          {cards.map(({ label, value, icon: Icon, tone, note }) => (
            <article className={`activity-summary-card ${tone}`} key={label}>
              <div className="activity-summary-icon">
                <Icon size={22} />
              </div>
              <strong>{isLoading ? "-" : value}</strong>
              <h2>{label}</h2>
              {note && <p>{note}</p>}
            </article>
          ))}
        </section>

        <section className="activity-visual-grid">
          <article className="activity-chart-panel">
            <div className="activity-panel-header">
              <div>
                <h2>{activityTitle}</h2>
                <p>
                  Each bar is the number of LOGIN_SUCCESS events in that period.
                </p>
              </div>
              <div className="activity-range-menu">
                <button
                  type="button"
                  className={`activity-range-trigger ${isRangeMenuOpen ? "open" : ""}`}
                  onClick={() => setIsRangeMenuOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={isRangeMenuOpen}
                >
                  {
                    ACTIVITY_RANGE_OPTIONS.find(
                      (option) => option.value === activityRange,
                    )?.label
                  }
                  <span aria-hidden="true">⌄</span>
                </button>
                {isRangeMenuOpen && (
                  <div className="activity-range-options" role="listbox">
                    {ACTIVITY_RANGE_OPTIONS.map((option) => (
                      <button
                        type="button"
                        role="option"
                        aria-selected={activityRange === option.value}
                        className={
                          activityRange === option.value ? "selected" : ""
                        }
                        key={option.value}
                        onClick={() => selectActivityRange(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {activityRange === "custom" && (
              <div
                className="activity-custom-date-control"
                ref={calendarControlRef}
              >
                <button
                  type="button"
                  className="activity-custom-date-trigger"
                  onClick={() => {
                    setPendingStartDate(null);
                    setCalendarMonth(parseLocalDate(customStartDate));
                    setIsMonthMenuOpen(false);
                    setIsYearMenuOpen(false);
                    setIsCalendarOpen((open) => !open);
                  }}
                  aria-expanded={isCalendarOpen}
                >
                  <CalendarDays size={15} />
                  <div>
                    <span>From</span>
                    <strong>
                      {parseLocalDate(customStartDate).toLocaleDateString([], {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </strong>
                  </div>
                  <span aria-hidden="true">→</span>
                  <div>
                    <span>To</span>
                    <strong>
                      {parseLocalDate(customEndDate).toLocaleDateString([], {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </strong>
                  </div>
                </button>
                {isCalendarOpen && (
                  <div className="activity-custom-date-range">
                    <div className="activity-calendar-instruction">
                      {pendingStartDate
                        ? `From ${pendingStartDate.toLocaleDateString()} selected. Now select To date.`
                        : "Select From date"}
                    </div>
                    <div className="activity-calendar-caption">
                      <button
                        type="button"
                        onClick={() => changeCalendarMonth(-1)}
                        disabled={
                          calendarMonth.getFullYear() === 2000 &&
                          calendarMonth.getMonth() === 0
                        }
                        aria-label="Previous month"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <div className="activity-calendar-caption-select">
                        <button
                          type="button"
                          onClick={() => {
                            setIsMonthMenuOpen((open) => !open);
                            setIsYearMenuOpen(false);
                          }}
                        >
                          {CALENDAR_MONTHS[calendarMonth.getMonth()]}
                        </button>
                        {isMonthMenuOpen && (
                          <div className="activity-calendar-menu">
                            {CALENDAR_MONTHS.map((month, index) => (
                              <button
                                type="button"
                                disabled={
                                  calendarMonth.getFullYear() ===
                                    today.getFullYear() &&
                                  index > today.getMonth()
                                }
                                className={
                                  calendarMonth.getMonth() === index
                                    ? "selected"
                                    : ""
                                }
                                key={month}
                                onClick={() => {
                                  setCalendarMonth(
                                    new Date(
                                      calendarMonth.getFullYear(),
                                      index,
                                    ),
                                  );
                                  setIsMonthMenuOpen(false);
                                }}
                              >
                                {month}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="activity-calendar-caption-select year">
                        <button
                          type="button"
                          onClick={() => {
                            setIsYearMenuOpen((open) => !open);
                            setIsMonthMenuOpen(false);
                          }}
                        >
                          {calendarMonth.getFullYear()}
                        </button>
                        {isYearMenuOpen && (
                          <div className="activity-calendar-menu">
                            {calendarYears.map((year) => (
                              <button
                                type="button"
                                className={
                                  calendarMonth.getFullYear() === year
                                    ? "selected"
                                    : ""
                                }
                                key={year}
                                onClick={() => {
                                  const month =
                                    year === today.getFullYear()
                                      ? Math.min(
                                          calendarMonth.getMonth(),
                                          today.getMonth(),
                                        )
                                      : calendarMonth.getMonth();
                                  setCalendarMonth(new Date(year, month));
                                  setIsYearMenuOpen(false);
                                }}
                              >
                                {year}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => changeCalendarMonth(1)}
                        disabled={
                          calendarMonth.getFullYear() === today.getFullYear() &&
                          calendarMonth.getMonth() === today.getMonth()
                        }
                        aria-label="Next month"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                    <DayPicker
                      animate
                      mode="range"
                      startMonth={new Date(2000, 0)}
                      endMonth={today}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      hideNavigation
                      selected={customDateRange}
                      onDayClick={handleCustomDayClick}
                      disabled={
                        pendingStartDate
                          ? [{ after: today }, { before: pendingStartDate }]
                          : { after: today }
                      }
                      showOutsideDays
                    />
                  </div>
                )}
              </div>
            )}
            <div className="activity-chart-body">
              {loginActivity.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={loginActivity}
                    margin={{ top: 12, right: 10, left: -22, bottom: 0 }}
                  >
                    <CartesianGrid stroke="#e7edf5" vertical={false} />
                    <XAxis
                      dataKey="label"
                      interval="preserveStartEnd"
                      minTickGap={24}
                      tick={{ fontSize: 10 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      tick={{ fontSize: 11 }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip cursor={{ fill: "#eff6ff" }} />
                    <Bar
                      dataKey="count"
                      name="Logins"
                      fill="#1686f8"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="activity-empty">Loading activity...</div>
              )}
            </div>
          </article>

          <article className="activity-chart-panel">
            <div className="activity-panel-header">
              <h2>Session Status Breakdown</h2>
            </div>
            <div className="session-chart-layout">
              <div className="session-donut-wrap">
                {sessionData.length ? (
                  <>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sessionData}
                          dataKey="value"
                          nameKey="name"
                          innerRadius="58%"
                          outerRadius="82%"
                          stroke="none"
                        >
                          {sessionData.map((item) => (
                            <Cell
                              key={item.key}
                              fill={SESSION_COLORS[item.key] || "#94a3b8"}
                            />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="session-donut-total">
                      <strong>{totalSessions}</strong>
                      <span>Total Sessions</span>
                    </div>
                  </>
                ) : (
                  <div className="activity-empty">
                    {isLoading ? "Loading sessions..." : "No session data."}
                  </div>
                )}
              </div>
              <div className="session-legend">
                {sessionData.map((item) => (
                  <div key={item.key}>
                    <span
                      className="session-legend-dot"
                      style={{
                        background: SESSION_COLORS[item.key] || "#94a3b8",
                      }}
                    />
                    <span>{item.name}</span>
                    <strong>{item.value}</strong>
                    <small>
                      {totalSessions
                        ? `(${Math.round((item.value / totalSessions) * 100)}%)`
                        : "(0%)"}
                    </small>
                  </div>
                ))}
              </div>
            </div>
          </article>
        </section>

        <section className="quick-actions-card">
          <h2 className="card-title">Quick Actions</h2>
          <div className="quick-actions-grid">
            <button
              className="action-button primary"
              onClick={() => navigate("/users")}
            >
              Manage Users
            </button>
            <button
              className="action-button secondary"
              onClick={() => navigate("/roles")}
            >
              Role Management
            </button>
            <button
              className="action-button department"
              onClick={() => navigate("/departments")}
            >
              Departments
            </button>
            <button
              className="action-button accent"
              onClick={() => navigate("/transactions")}
            >
              Transactions
            </button>
            <button
              className="action-button info"
              onClick={() => navigate("/field-master")}
            >
              Field Master
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Dashboard;
